/** 관리자 콘솔의 [검수하기] 가 사용자 사이트를 여는 주소의 표지 — `…?review=1`. */
const ENTRY_PARAM = "review";
const STORAGE_KEY = "ds-review-mode";
const ON = "1";

/**
 * 이 탭이 검수 모드인가 — **페이지 로드마다 한 번**, 이 모듈이 평가될 때 정한다(`review-mode`).
 *
 * 모듈 평가는 이 모듈을 들여오는 어떤 코드보다 앞선다. 그래서 그 로드의 첫 백엔드 요청이 나가기 전에 모드가 정해져 있다 —
 * 늦으면 마스킹된 상태가 조회 캐시에 들어가(`staleTime` 10분) 검수 주소가 `/404` 로 간다. 모드를 백엔드에 싣는 자리
 * (`reviewModeHeader`)가 이 모듈 위에 서는 까닭이다.
 *
 * 이 모듈은 아무것도 들여오지 않는다 — 상태 어휘(`constants`)의 부분 완료 술어(2D 완료·3D 완료)가 이 값을 읽는다. 설정(`awsconfig`)을
 * 들여오면 두 술어를 쓰는 모든 모듈이 설정의 top-level await 를 물고, `constants` 와 순환한다.
 *
 * 한 번 정한 값은 그 페이지가 사는 동안 바뀌지 않는다. 조회 캐시도 한 페이지 수명만 살므로 두 모드의 응답이 섞이지 않고,
 * 끄는 길은 새 페이지를 여는 `exitReviewMode` 하나다.
 */
const reviewMode = resolveReviewMode();

/**
 * 이 탭이 검수 모드인가. 화면은 이 값으로 **열지 않는다** — 무엇을 실제 상태로 보일지는 서버가 정하고, 화면은 서버가 준
 * 상태를 읽는다. 읽는 곳은 셋이다: 모드를 드러내는 표시(`ReviewModeIndicator`), 모드를 백엔드에 싣는 자리
 * (`reviewModeHeader`), 그리고 검수 모드에서만 오는 값을 보통 탭에서 지우는 부분 완료 술어
 * (`is2dCompletedTimeProject`·`is3dCompletedTimeProject`).
 */
export function isReviewMode(): boolean {
  return reviewMode;
}

/**
 * 검수 모드를 끝낸다 — 탭에서 지우고 **프로젝트 목록을 새로 연다**. 새 페이지 로드가 모드와 함께 조회 캐시를 버려, 검수
 * 모드로 받은 응답이 남지 않는다.
 *
 * 끝낸 자리와 무관하게 목록을 여는 까닭: 검수 모드는 보류된 시계열의 2D·3D 화면을 보러 켜고, 그 자리를 다시 열면 검수 모드가
 * 아니어서 진입 판정이 닫혀 `/404` 로 간다.
 */
export function exitReviewMode(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // 저장소를 쓸 수 없는 탭은 검수 모드가 될 수 없다 — 지울 것이 없다.
  }
  window.location.replace("/projects-list");
}

/**
 * 주소의 `review=1` 은 이 탭을 검수 모드로 만들고, 탭의 저장소가 그것을 다음 로드로 잇는다.
 *
 * 판정은 저장소에서 **되읽은** 값이다 — 저장소가 막힌 탭(접근 자체가 던지는 브라우저 포함)은 모드를 이어 갈 수 없으므로
 * 켜지 않는다. 새로고침에 꺼지는 반쪽 모드보다, 표시가 서지 않는 쪽이 정직하다.
 */
function resolveReviewMode(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const isEntering =
      new URLSearchParams(window.location.search).get(ENTRY_PARAM) === ON;
    if (isEntering) window.sessionStorage.setItem(STORAGE_KEY, ON);
    return window.sessionStorage.getItem(STORAGE_KEY) === ON;
  } catch {
    return false;
  }
}
